import { FC } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';

import {
  ActiveWorkspace,
  AILaunchWidget,
  MaturityHero,
  OrgProfileWidget,
  RemediationWidget,
  StatStrip,
  WelcomeBar,
} from '@features/dashboard';
import { useFeatureFlag } from '@hooks';
import { FeatureFlagKey } from '@libs/constants';

/**
 * Authenticated landing page. A welcome header + assessment stat strip above a
 * responsive 2×2 widget grid (maturity hero, active workspace, organization
 * profile, remediation, AI launcher). Mounts inside `AppLayout`'s content
 * outlet.
 */
export const DashboardPage: FC = observer(() => {
  const isRemediationEnabled = useFeatureFlag(FeatureFlagKey.REMEDIATION);

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: 'common.white',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <WelcomeBar />
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2.5, md: 3.75 }, py: 2.75 }}>
        <StatStrip />
        <Box
          sx={{
            display: 'grid',
            gap: 2.25,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.55fr) minmax(0,1fr)' },
            alignItems: 'stretch',
          }}
        >
          <MaturityHero />
          <ActiveWorkspace />
          <OrgProfileWidget />
          {/* Self-hides when nothing is in flight, so it only claims a grid
              slot when there is remediation to report. Also hidden outright
              when the runtime remediation feature flag is off. */}
          {isRemediationEnabled && <RemediationWidget />}
          <AILaunchWidget />
        </Box>
      </Box>
    </Box>
  );
});
